import React from 'react';
import { useEffect, useState } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useToast } from '../hooks/useToast';
import { authService } from '@/api';
import { useAuthFlowBlocker } from '@/navigation/useAuthFlowBlocker';
import { AuthPageShell } from '../components/ui/AuthPageShell';
import { AppButton } from '../components/ui/AppButton';
import {
  getPasswordMatchError,
  getPasswordValidationError,
  PASSWORD_MIN_LENGTH,
} from '../utils/fieldValidation';

export function ChangePasswordPage() {
  const { user, updateCurrentUser } = useAuth();
  useAuthFlowBlocker(
    !!user?.mustChangePassword,
    'You must change your password before leaving this page.'
  );
  const { showToast } = useToast();
  const navigate = useNavigate();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (!user) return;
    if (!user.mustChangePassword) {
      navigate(`/dashboard/${user.role}`, {
        replace: true,
      });
    }
  }, [user, navigate]);
  if (!user) return <Navigate to="/login" replace={true} />;

  const submit = async () => {
    if (!currentPassword) {
      showToast('Please enter your current password', 'error');
      return;
    }
    const passwordErr = getPasswordValidationError(newPassword);
    if (passwordErr) {
      showToast(passwordErr, 'error');
      return;
    }
    const matchErr = getPasswordMatchError(newPassword, confirmPassword);
    if (matchErr) {
      showToast(matchErr, 'error');
      return;
    }
    setLoading(true);
    try {
      await authService.changePassword({
        currentPassword,
        newPassword,
      });
      updateCurrentUser({
        mustChangePassword: false,
      });
      showToast('Password updated', 'success');
      navigate(`/dashboard/${user.role}`, { replace: true });
    } catch (e) {
      showToast(e instanceof Error ? e.message : 'Failed to update password', 'error');
    } finally {
      setLoading(false);
    }
  };

  const inputClass =
    'w-full min-h-[44px] px-4 py-3 border border-slate-300 rounded-xl bg-white text-slate-900 transition-shadow duration-200 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-400';

  return (
    <AuthPageShell title="Change Password">
      {user.mustChangePassword ? (
        <p className="mb-4 rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-700">
          You must change your password before continuing.
        </p>
      ) : null}
      <div className="space-y-4">
        <div>
          <label className="mb-2 block text-sm font-bold text-slate-700">Current password</label>
          <input
            type="password"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            className={inputClass}
            autoComplete="current-password"
          />
        </div>
        <div>
          <label className="mb-2 block text-sm font-bold text-slate-700">New password</label>
          <input
            type="password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            className={inputClass}
            autoComplete="new-password"
            minLength={PASSWORD_MIN_LENGTH}
          />
          <p className="mt-1 text-xs text-slate-500">At least {PASSWORD_MIN_LENGTH} characters.</p>
        </div>
        <div>
          <label className="mb-2 block text-sm font-bold text-slate-700">
            Confirm new password
          </label>
          <input
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            className={inputClass}
            autoComplete="new-password"
          />
        </div>
        <AppButton
          type="button"
          variant="primary"
          size="lg"
          fullWidth
          disabled={loading}
          onClick={() => void submit()}
        >
          {loading ? 'Updating…' : 'Update Password'}
        </AppButton>
      </div>
    </AuthPageShell>
  );
}
